import { defineStore } from 'pinia';
import { getData, postData, putData } from '@/services/axios-service';
import {
  Device,
  Notification,
  NotificationsState,
} from '@/interfaces/notifications/notifications.interface';
import { PaginationResponse } from '@/interfaces/meta.interface';
import { BASE_URL } from '@/utils/constants/api-constants';

const INITIAL_STATE = {
  pagination: {
    count: 0,
    limit: 10,
    page: 1,
    pages: 0,
  },
  notifications: [],
  notificationsInPage: [],
  isRead: false,
};

export const useNotificationsStore = defineStore({
  id: 'useNotificationsStore',
  state: (): NotificationsState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    async registerDevice(deviceForm: Device) {
      try {
        const response = await postData<Device>(`${BASE_URL.NOTIFICATIONS}devices`, {
          ...deviceForm,
        });

        const { success, data, error } = response;

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async getNotifications(page: number = 1) {
      try {
        if (page) this.pagination.page = page;

        const params = {
          limit: this.pagination.limit,
          page: this.pagination.page,
          ...(this.isRead === false && { isRead: false }),
        };
        const response = await getData<PaginationResponse<Notification[]>>(
          `${BASE_URL.NOTIFICATIONS}notifications`,
          params,
        );

        const { success, data, error } = response;

        if (success && data) {
          const { rows, count, limit, pages, page } = data;

          this.pagination.count = count;
          this.pagination.limit = limit;
          this.pagination.page = page;
          this.pagination.pages = pages;

          this.notifications = rows;
          return rows;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async getNotification(notificationId: string) {
      try {
        const response = await getData<Notification>(
          `${BASE_URL.NOTIFICATIONS}notifications/${notificationId}`,
        );

        const { success, data, error } = response;

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async markAllAsRead() {
      try {
        const response = await putData<Device>(
          `${BASE_URL.NOTIFICATIONS}notifications/mark-all-as-read`,
        );

        const { msg, error } = response;

        if (msg) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async markAsRead(notificationId: string) {
      try {
        const response = await putData<Device>(
          `${BASE_URL.NOTIFICATIONS}notifications/${notificationId}/mark-as-read`,
          {
            id: notificationId,
          },
        );

        const { success, data, error } = response;

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
  },
});
